/**
 * Mindmap API Service
 * Handles all API calls to the Cloudflare Worker
 */

import accountService from './accountService';

const API_BASE = import.meta.env.VITE_API_URL || '';

class MindmapService {
  async fetchMindmaps() {
    const response = await accountService.authenticatedFetch(`${API_BASE}/api/mindmaps`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error);
    return data.mindmaps;
  }

  async fetchMindmap(id) {
    const response = await accountService.authenticatedFetch(`${API_BASE}/api/mindmaps/${id}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error);
    return data;
  }

  async createMindmap(title = 'Untitled Mindmap', description = '') {
    const response = await accountService.authenticatedFetch(`${API_BASE}/api/mindmaps`, {
      method: 'POST',
      body: JSON.stringify({ title, description }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error);
    return data.mindmap;
  }

  async updateMindmap(id, updates) {
    const response = await accountService.authenticatedFetch(`${API_BASE}/api/mindmaps/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error);
    return data.mindmap;
  }

  async deleteMindmap(id) {
    const response = await accountService.authenticatedFetch(`${API_BASE}/api/mindmaps/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error);
    }
    return true;
  }

  async createNode(mindmapId, nodeData) {
    const response = await accountService.authenticatedFetch(
      `${API_BASE}/api/mindmaps/${mindmapId}/nodes`,
      { method: 'POST', body: JSON.stringify(nodeData) }
    );
    const data = await response.json();
    if (!response.ok) throw new Error(data.error);
    return data.node;
  }

  async updateNode(nodeId, nodeData) {
    const response = await accountService.authenticatedFetch(
      `${API_BASE}/api/nodes/${nodeId}`,
      { method: 'PUT', body: JSON.stringify(nodeData) }
    );
    const data = await response.json();
    if (!response.ok) throw new Error(data.error);
    return data.node;
  }

  async deleteNode(nodeId) {
    const response = await accountService.authenticatedFetch(
      `${API_BASE}/api/nodes/${nodeId}`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error);
    }
    return true;
  }

  async batchUpdate(mindmapId, nodes, edges, mindmap) {
    const response = await accountService.authenticatedFetch(
      `${API_BASE}/api/mindmaps/batch`,
      {
        method: 'PUT',
        body: JSON.stringify({ mindmapId, nodes, edges, mindmap }),
      }
    );
    const data = await response.json();
    if (!response.ok) throw new Error(data.error);
    return data;
  }

  async exportMindmap(mindmapId) {
    const { mindmap, nodes, edges } = await this.fetchMindmap(mindmapId);
    return {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      mindmap,
      nodes,
      edges,
    };
  }

  async importMindmap(jsonData) {
    const { mindmap, nodes, edges } = jsonData;
    
    // Create new mindmap with imported title
    const newMindmap = await this.createMindmap(
      `${mindmap.title} (Imported)`,
      mindmap.description
    );
    
    // Map old node IDs to new IDs
    const nodeIdMap = {};
    const newNodes = nodes.map(node => {
      const newId = crypto.randomUUID();
      nodeIdMap[node.id] = newId;
      return {
        ...node,
        id: newId,
        parentId: node.parentId ? nodeIdMap[node.parentId] : null,
      };
    });

    // Map edge IDs and update references
    const newEdges = edges.map(edge => ({
      ...edge,
      id: crypto.randomUUID(),
      source: nodeIdMap[edge.source] || edge.source,
      target: nodeIdMap[edge.target] || edge.target,
    }));

    // Batch update with new nodes and edges
    await this.batchUpdate(newMindmap.id, newNodes, newEdges, null);
    
    return newMindmap;
  }
}

export default new MindmapService();
